import { Box, Button, Typography, Container } from '@mui/material';
import { imageAssetUrl } from '../../../../../../../utils/publicAssets';

const nftShowcase = imageAssetUrl('nft-showcase.png');

const RPG_UTILITY_URL =
  'https://store.epicgames.com/en-US/p/incrypt-shadows-of-satoshi-34602b';

const RaffleNFTShowcase = () => {
  const scrollToPackages = () => {
    document.getElementById('raffle-packages')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: 8, md: 12, lg: 6 },
        pb: { xs: 8, md: 12 },
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
            mb: 5.8,
            px: 4,
            py: 3.5,
            borderRadius: 4,
            background:
              'linear-gradient(135deg, #07111f 0%, #0f172a 55%, #052e2f 100%)',
            border: '1px solid rgba(36,191,189,0.32)',
            boxShadow: '0 22px 70px rgba(15,23,42,0.18)'
          }}
        >
          <Box>
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: 28,
                fontWeight: 950,
                lineHeight: 1.1
              }}
            >
              Every entry includes Digital Collectible rewards.
            </Typography>
            <Typography
              sx={{
                mt: 1,
                color: 'rgba(255,255,255,0.72)',
                fontSize: 16,
                fontWeight: 700
              }}
            >
              Pick a pack, enter the INC Token draw, and unlock collectibles for
              the Incrypt universe.
            </Typography>
          </Box>

          <Button
            onClick={scrollToPackages}
            sx={{
              flexShrink: 0,
              px: 4,
              py: 1.3,
              borderRadius: 999,
              backgroundColor: '#16a34a',
              color: '#ffffff',
              fontSize: 17,
              fontWeight: 950,
              boxShadow: '0 14px 30px rgba(22,163,74,0.32)',
              '&:hover': {
                backgroundColor: '#15803d'
              }
            }}
          >
            Choose Your Pack
          </Button>
        </Box>

        {/* NFT Image */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 6
          }}
        >
          <Box
            component="img"
            src={nftShowcase}
            alt="Incrypt Digital Collectibles"
            sx={{
              width: '100%',
              maxWidth: 900,
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Heading */}
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            mb: 3,
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: {
              xs: 32,
              sm: 40,
              md: 48,
              lg: 56
            },
            color: 'black'
          }}
        >
          Our Digital Collectibles
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            textAlign: 'center',
            maxWidth: 720,
            mx: 'auto',
            fontStyle: 'italic',
            color: 'black',
            fontSize: { xs: 15, md: 18 },
            lineHeight: 1.7
          }}
        >
          Incrypt Digital Collectibles are more than collectibles – they are key assets in the
          Incrypt universe. From Trading Card Game packs to RPG gear and
          characters, each Digital Collectible holds unique utility. Equip, trade, and compete
          as you unlock their full potential across our ecosystem.
        </Typography>

        {/* Features Grid */}
        <Box
          sx={{
            mt: 8,
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, 1fr)'
            },
            maxWidth: 900,
            mx: 'auto'
          }}
        >
          <FeatureCard
            emoji="💎"
            title="True Ownership"
            description="Trade and sell your Digital Collectibles on the open marketplace"
          />

          <FeatureCard
            emoji="🎮"
            title="Trading Card Game"
            description="Collect unique characters and build your ultimate deck"
          />

          <FeatureCard
            emoji="⚔️"
            title="RPG Utility"
            description="Equip gear and characters in the Incrypt RPG universe"
            href={RPG_UTILITY_URL}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default RaffleNFTShowcase;

/* ---------- Sub Components ---------- */

const FeatureCard = ({
  emoji,
  title,
  description,
  href
}: {
  emoji: string;
  title: string;
  description: string;
  href?: string;
}) => {
  const isClickable = href !== undefined;
  const hasLink = Boolean(href);

  return (
    <Box
      {...(isClickable
        ? {
            component: 'a',
            href: hasLink ? href : undefined,
            target: '_blank',
            rel: 'noopener noreferrer'
          }
        : {})}
      sx={{
        textAlign: 'center',
        p: 3,
        borderRadius: 3,
        color: 'inherit',
        textDecoration: 'none',
        cursor: isClickable ? 'pointer' : 'default',

        // Tailwind: bg-incrypt-cyan/5 border-incrypt-cyan/20 backdrop-blur-sm
        background: 'white',
        border: '1px solid black',
        backdropFilter: 'blur(6px)',

        transition: isClickable
          ? 'transform 0.25s ease, box-shadow 0.25s ease'
          : 'none',
        ...(isClickable
          ? {
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)'
              }
            }
          : {})
      }}
    >
      <Typography sx={{ fontSize: 32, mb: 1 }}>{emoji}</Typography>

      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 18,
          mb: 1,
          color: 'black'
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: 'black',
          lineHeight: 1.6
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};
